import {
  IMailingRule,
  IExpandedMailingRule,
  ISystemField,
  ISelectedEPS,
  ISelectedEPSField,
  IPossiblyDraftMailingRule,
  AggregationTypeEnum,
} from 'interfaces/MailingRule';
import { IRule, ICondition } from 'interfaces/Rule';
import { store } from 'store';
import { normalizeObject } from 'utils/files';
import { ParseRangeField2RuleOptions } from './interfaces';

const DEFAULT_RANGE = { min: 0, max: 0 };

const expandRangeRule = (fact: string, rules: IRule[]) => {
  const rule = rules.find((r) => r.fact === fact);

  if (!rule) {
    return undefined;
  }

  const lowerThanValue = rule.conditions.find((c) =>
    c.operator.toLocaleLowerCase().startsWith('lower'),
  );
  const greaterThanValue = rule.conditions.find((c) =>
    c.operator.toLocaleLowerCase().startsWith('greater'),
  );

  const min = greaterThanValue
    ? parseInt(greaterThanValue.value.toString())
    : undefined;
  const max = lowerThanValue
    ? parseInt(lowerThanValue.value.toString())
    : undefined;

  return {
    min,
    max,
  };
};

const expandMultipleSelectionRule = (fact: string, rules: IRule[]) => {
  const rule = rules.find((r) => r.fact === fact);
  if (!rule) {
    return [];
  }

  return rule.conditions.map((c) => c.value.toString());
};

const expandBooleanRule = (fact: string, rules: IRule[]) => {
  return !!rules.find((r) => r.fact === fact);
};

const expandSystemsRules = (rules: IRule[]) => {
  const {
    list: { mailingRuleSystemFields, mailingRuleSystemAndSubSystemByValue },
  } = store.getState().parameters;
  const systemsParsed: ISystemField = mailingRuleSystemFields.reduce(
    (systems, id) => {
      systems[id] = [];
      return systems;
    },
    {} as ISystemField,
  );

  const subSystemRules = rules.filter((r) => r.fact === 'SUBSYSTEM');
  subSystemRules.forEach((sub) =>
    sub.conditions.forEach((cond) => {
      const value = String(cond.value);
      const { system, subSystem } = mailingRuleSystemAndSubSystemByValue[value];
      systemsParsed[system].push(subSystem);
    }),
  );

  return systemsParsed;
};

const expandSelectedEPS = (
  collectRuleStrategies: ISelectedEPS[],
): ISelectedEPSField =>
  collectRuleStrategies.reduce((all, current: ISelectedEPS) => {
    if (current.epsId === null) {
      return all;
    }

    const companyName =
      typeof current.epsId === 'string' ? 'Erro' : current.epsId.companyName;
    const epsId =
      typeof current.epsId === 'string' ? current.epsId : current.epsId._id;

    all[epsId] = {
      ...current,
      remunerationType: current.remunerationType || '',
      remunerationBonusId: current.remunerationBonusId?._id || '',
      remunerationCommissionId: current.remunerationCommissionId?._id || '',
      remunerationFixedIds:
        current.remunerationFixedIds?.map((fixed) => fixed._id) || [],
      remunerationGoalIds:
        current.remunerationGoalIds?.map((goal) => goal._id) || [],
      companyName,
    };
    return all;
  }, {} as ISelectedEPSField);

const expandMailingRule = (
  rawMailingRule: IMailingRule,
): IExpandedMailingRule => {
  const parsedObject = {} as IExpandedMailingRule;

  parsedObject._id = rawMailingRule._id || '';
  parsedObject.id = rawMailingRule._id || '';
  parsedObject.serviceCategory = rawMailingRule.serviceCategory?._id || '';
  parsedObject.name = rawMailingRule.name;
  parsedObject.aggregationType =
    rawMailingRule.aggregationType || AggregationTypeEnum.contract;

  parsedObject.customerAging =
    expandRangeRule('CUSTOMER_AGING', rawMailingRule.rules) || DEFAULT_RANGE;
  const parsedContractAging =
    expandRangeRule('CONTRACT_AGING', rawMailingRule.rules) || DEFAULT_RANGE;
  parsedObject.shouldFilterOnContractAging = parsedContractAging.max
    ? true
    : false;
  parsedObject.contractAging = parsedContractAging || DEFAULT_RANGE;

  parsedObject.customerType = expandMultipleSelectionRule(
    'CUSTOMER_TYPE',
    rawMailingRule.rules,
  );
  parsedObject.corporateType = expandMultipleSelectionRule(
    'CORPORATE',
    rawMailingRule.rules,
  );
  parsedObject.propensity = expandMultipleSelectionRule(
    'PROPENSITY',
    rawMailingRule.rules,
  );
  parsedObject.systems = expandSystemsRules(rawMailingRule.rules);

  parsedObject.billingType = expandMultipleSelectionRule(
    'BILLING_TYPE',
    rawMailingRule.rules,
  );
  parsedObject.uf = expandMultipleSelectionRule('UF', rawMailingRule.rules);
  parsedObject.excludeExemption = expandBooleanRule(
    'EXCLUDE_EXEMPTION',
    rawMailingRule.rules,
  );

  parsedObject.activeOffer = expandMultipleSelectionRule(
    'ACTIVE_OFFER',
    rawMailingRule.rules,
  );

  parsedObject.customerDebtsSum =
    expandRangeRule('CUSTOMER_DEBTS_SUM', rawMailingRule.rules) ||
    DEFAULT_RANGE;
  parsedObject.contractDebtsSum =
    expandRangeRule('CONTRACT_DEBTS_SUM', rawMailingRule.rules) ||
    DEFAULT_RANGE;
  parsedObject.invoiceDebtAmount =
    expandRangeRule('INVOICE_DEBT_AMOUNT', rawMailingRule.rules) ||
    DEFAULT_RANGE;
  parsedObject.customerContractsSum =
    expandRangeRule('CUSTOMER_CONTRACTS_SUM', rawMailingRule.rules) ||
    DEFAULT_RANGE;
  parsedObject.contractInvoicesSum =
    expandRangeRule('CONTRACT_INVOICES_SUM', rawMailingRule.rules) ||
    DEFAULT_RANGE;

  parsedObject.permanenceType = (function () {
    if (rawMailingRule.permanenceInDays) return 'permanenceInDays';
    else if (rawMailingRule.permanenceUntilDate) return 'permanenceUntilDate';
    return '';
  })();

  parsedObject.permanenceInDays = rawMailingRule.permanenceInDays || 0;
  parsedObject.permanenceUntilDate =
    rawMailingRule.permanenceUntilDate || '2000-01-01';
  parsedObject.paymentRemunerationDays =
    rawMailingRule.paymentRemunerationDays || 0;

  parsedObject.weekDaysToExecute =
    1 || rawMailingRule.weekDaysToExecute[0] || -1;
  parsedObject.debtsDistribution = rawMailingRule.debtsDistribution || '';

  const parsedInvoiceAgingToInclude = expandRangeRule(
    'INVOICE_AGING_TO_INCLUDE',
    rawMailingRule.rules,
  );
  parsedObject.shouldIncludeNewInvoice = !!parsedInvoiceAgingToInclude;
  parsedObject.invoiceAgingToInclude = parsedInvoiceAgingToInclude;

  parsedObject.periodUpdateDebt = rawMailingRule.periodUpdateDebt || 0;
  parsedObject.selectedEPS = expandSelectedEPS(
    rawMailingRule.collectRuleStrategies,
  );
  parsedObject.status = rawMailingRule.status;

  return parsedObject;
};

const parseRangeField2Rule = (
  fact: string,
  min: number | undefined,
  max: number | undefined,
  { minCanBeZero, type, priorityOfItemRule }: ParseRangeField2RuleOptions = {},
) => {
  type = type || 'ALL';
  priorityOfItemRule = priorityOfItemRule || 1;

  const conditions: ICondition[] = [];
  if (min || (min === 0 && minCanBeZero)) {
    conditions.push({ operator: 'GREATER_THAN_INCLUSIVE', value: min });
  }
  if (max) {
    conditions.push({ operator: 'LOWER_THAN_INCLUSIVE', value: max });
  }

  if (!conditions.length) {
    return undefined;
  }

  return { type, fact, priorityOfItemRule, conditions };
};

const parseMultipleSelectionField2Rule = (
  fact: string,
  values: string[],
  type: 'ALL' | 'ANY' = 'ANY',
  priorityOfItemRule = 1,
) => {
  const conditions: ICondition[] = [];

  values.map((value) => conditions.push({ operator: 'EQUAL', value }));

  if (!conditions.length) {
    return undefined;
  }

  return { type, fact, priorityOfItemRule, conditions };
};

const parseBooleanField2Rule = (
  fact: string,
  value: boolean,
  type: 'ALL' | 'ANY' = 'ALL',
  priorityOfItemRule = 1,
) => {
  return value
    ? {
        type,
        fact,
        priorityOfItemRule,
        conditions: [{ operator: 'EQUAL', value: true }],
      }
    : undefined;
};

const parseSystemsField2Rules = (systems: ISystemField): IRule[] => {
  const { mailingRuleSystemsValue } = store.getState().parameters.list;

  const ruleSystems: IRule = {
    type: 'ANY',
    fact: 'SYSTEM',
    priorityOfItemRule: 2,
    conditions: [],
  };
  const ruleSubSystems: IRule = {
    type: 'ANY',
    fact: 'SUBSYSTEM',
    priorityOfItemRule: 1,
    conditions: [],
  };

  Object.entries(systems).forEach(([system, subSystems]) => {
    const systemCondition: ICondition = {
      operator: 'EQUAL',
      value: mailingRuleSystemsValue[system],
    };
    if (subSystems.length) {
      ruleSystems.conditions.push(systemCondition);

      subSystems.forEach((subSystem) => {
        const subSystemsCondition: ICondition = {
          operator: 'EQUAL',
          value: subSystem,
        };
        ruleSubSystems.conditions.push(subSystemsCondition);
      });
    }
  });
  return [ruleSystems, ruleSubSystems];
};

const mailingRuleForm2Raw = (
  formMailingRule: IExpandedMailingRule,
): IPossiblyDraftMailingRule => {
  const parsedObject = {} as IPossiblyDraftMailingRule;

  parsedObject.serviceCategory = formMailingRule.serviceCategory || undefined;
  parsedObject.name = formMailingRule.name;
  parsedObject.aggregationType = formMailingRule.aggregationType || undefined;

  parsedObject.rules = [];
  if (formMailingRule.customerAging.max > 0) {
    const customerAging = parseRangeField2Rule(
      'CUSTOMER_AGING',
      formMailingRule.customerAging.min,
      formMailingRule.customerAging.max,
      { minCanBeZero: true },
    );
    if (customerAging) parsedObject.rules.push(customerAging);
  }

  if (
    formMailingRule.shouldFilterOnContractAging &&
    formMailingRule.contractAging.max > 0
  ) {
    const contractAging = parseRangeField2Rule(
      'CONTRACT_AGING',
      formMailingRule.contractAging.min,
      formMailingRule.contractAging.max,
      { minCanBeZero: true },
    );
    if (contractAging) parsedObject.rules.push(contractAging);
  }

  const customerType = parseMultipleSelectionField2Rule(
    'CUSTOMER_TYPE',
    formMailingRule.customerType,
  );
  if (customerType) parsedObject.rules.push(customerType);

  const corporateType = parseMultipleSelectionField2Rule(
    'CORPORATE',
    formMailingRule.corporateType,
  );
  if (corporateType) parsedObject.rules.push(corporateType);

  const propensity = parseMultipleSelectionField2Rule(
    'PROPENSITY',
    formMailingRule.propensity,
  );
  if (propensity) parsedObject.rules.push(propensity);

  const systems = parseSystemsField2Rules(formMailingRule.systems);
  parsedObject.rules.push(...systems);

  const billingType = parseMultipleSelectionField2Rule(
    'BILLING_TYPE',
    formMailingRule.billingType,
  );
  if (billingType) parsedObject.rules.push(billingType);

  const uf = parseMultipleSelectionField2Rule('UF', formMailingRule.uf);
  if (uf) parsedObject.rules.push(uf);

  const excludeExemption = parseBooleanField2Rule(
    'EXCLUDE_EXEMPTION',
    formMailingRule.excludeExemption,
  );
  if (excludeExemption) parsedObject.rules.push(excludeExemption);

  const activeOffer = parseMultipleSelectionField2Rule(
    'ACTIVE_OFFER',
    formMailingRule.activeOffer,
  );
  if (activeOffer) parsedObject.rules.push(activeOffer);

  if (formMailingRule.customerDebtsSum) {
    const customerDebtsSum = parseRangeField2Rule(
      'CUSTOMER_DEBTS_SUM',
      formMailingRule.customerDebtsSum.min,
      formMailingRule.customerDebtsSum.max,
    );
    if (customerDebtsSum) parsedObject.rules.push(customerDebtsSum);
  }

  if (formMailingRule.contractDebtsSum) {
    const contractDebtsSum = parseRangeField2Rule(
      'CONTRACT_DEBTS_SUM',
      formMailingRule.contractDebtsSum.min,
      formMailingRule.contractDebtsSum.max,
    );
    if (contractDebtsSum) parsedObject.rules.push(contractDebtsSum);
  }

  if (formMailingRule.invoiceDebtAmount) {
    const invoiceDebtAmount = parseRangeField2Rule(
      'INVOICE_DEBT_AMOUNT',
      formMailingRule.invoiceDebtAmount.min,
      formMailingRule.invoiceDebtAmount.max,
    );
    if (invoiceDebtAmount) parsedObject.rules.push(invoiceDebtAmount);
  }

  if (formMailingRule.customerContractsSum) {
    const customerContractsSum = parseRangeField2Rule(
      'CUSTOMER_CONTRACTS_SUM',
      formMailingRule.customerContractsSum.min,
      formMailingRule.customerContractsSum.max,
    );
    if (customerContractsSum) parsedObject.rules.push(customerContractsSum);
  }

  if (formMailingRule.contractInvoicesSum) {
    const contractInvoicesSum = parseRangeField2Rule(
      'CONTRACT_INVOICES_SUM',
      formMailingRule.contractInvoicesSum.min,
      formMailingRule.contractInvoicesSum.max,
    );
    if (contractInvoicesSum) parsedObject.rules.push(contractInvoicesSum);
  }

  if (formMailingRule.permanenceType === 'permanenceInDays') {
    parsedObject.permanenceInDays =
      formMailingRule.permanenceInDays || undefined;
  } else if (formMailingRule.permanenceType === 'permanenceUntilDate') {
    parsedObject.permanenceUntilDate =
      String(new Date(formMailingRule.permanenceUntilDate).getTime()) ||
      undefined;
  }

  parsedObject.paymentRemunerationDays =
    formMailingRule.paymentRemunerationDays || undefined;

  parsedObject.debtsDistribution =
    formMailingRule.debtsDistribution || undefined;

  if (
    formMailingRule.shouldIncludeNewInvoice &&
    formMailingRule.invoiceAgingToInclude
  ) {
    const invoiceAgingToInclude = parseRangeField2Rule(
      'INVOICE_AGING_TO_INCLUDE',
      formMailingRule.invoiceAgingToInclude.min,
      formMailingRule.invoiceAgingToInclude.max,
    );
    if (invoiceAgingToInclude) parsedObject.rules.push(invoiceAgingToInclude);
  }

  parsedObject.periodUpdateDebt = formMailingRule.periodUpdateDebt || undefined;

  const selectedEPSEntries = Object.entries(formMailingRule.selectedEPS);
  if (selectedEPSEntries.length) {
    parsedObject.collectRuleStrategies = selectedEPSEntries.map(
      ([epsId, values]) => {
        const parsedValues = normalizeObject(Object.assign({}, values));
        parsedValues.remunerationType = 'EXEMPTED';

        return {
          epsId,
          distributionPercentage: parsedValues.distributionPercentage,
          remunerationType: parsedValues.remunerationType,
          remunerationCommissionId:
            parsedValues.remunerationType === 'COMMISSION'
              ? parsedValues.remunerationCommissionId
              : undefined,
          remunerationFixedIds:
            parsedValues.remunerationType === 'VALUE_FIXED'
              ? parsedValues.remunerationFixedIds
              : undefined,
          remunerationBonusId:
            parsedValues.remunerationType !== 'EXEMPTED'
              ? parsedValues.remunerationBonusId
              : undefined,
          remunerationGoalIds:
            parsedValues.remunerationType !== 'EXEMPTED'
              ? parsedValues.remunerationGoalIds
              : undefined,
        };
      },
    );
  }

  parsedObject.status = formMailingRule.status;

  return parsedObject;
};

export { expandMailingRule, mailingRuleForm2Raw, expandSelectedEPS };
